import React, { useCallback, useMemo, useState } from 'react';
import { RefreshControl, View } from 'react-native';
import { useQuery } from '@apollo/client';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import moment from 'moment';
import { Feather } from '@expo/vector-icons';

import { GET_MY_WARNINGS } from '../../graphql/people';
import { Text } from '../../components';
import { Card, EmptyState, Screen, Tag } from '../../design/components';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';

// Own issued warnings for the signed-in employee (the server scopes mobile reads to self).
const MyWarningsView = () => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const [isRefreshing, setIsRefreshing] = useState(false);

	const { data, error, loading, refetch } = useQuery(GET_MY_WARNINGS, {
		variables: {
			page: 0,
			perPage: 100,
			sortBy: 'issuedAt',
			sortDirection: -1,
			filter: { selfOnly: true },
		},
		fetchPolicy: 'cache-and-network',
		notifyOnNetworkStatusChange: true,
	});

	const warnings = useMemo(() => (
		data?.getEmployeeWarnings?.items || []
	), [data?.getEmployeeWarnings?.items]);

	useFocusEffect(useCallback(() => {
		refetch().catch(() => {});
	}, [refetch]));

	const refresh = useCallback(async () => {
		setIsRefreshing(true);
		try {
			await refetch();
		}
		finally {
			setIsRefreshing(false);
		}
	}, [refetch]);

	const openWarning = useCallback((warning) => {
		navigation.navigate('MyWarningDetailView', { warning });
	}, [navigation]);

	return (
		<Screen
			backLabel="My account"
			heading="Warnings"
			subheading="Warnings issued to you, including their acknowledgement status."
			isLoading={Boolean(loading && !warnings.length && !error)}
			refreshControl={(
				<RefreshControl
					refreshing={isRefreshing}
					onRefresh={refresh}
					tintColor={colors.accent}
				/>
			)}
		>
			{
				Boolean(error) && (
					<EmptyState
						tone="error"
						title="Unable to load warnings"
						message="Pull down to try again."
						actionLabel="Try again"
						onAction={refresh}
					/>
				)
			}

			{
				Boolean(!error && !loading && !warnings.length) && (
					<EmptyState
						icon="check-circle"
						title="No warnings"
						message="Warnings issued to you will appear here."
					/>
				)
			}

			{
				warnings.length > 0 && (
					<View style={styles.list}>
						{warnings.map((warning) => {
							const needsAcknowledgement = !warning.acknowledged;
							const title = warning.summary || warning.typeLabel || 'Warning';
							const meta = [
								warning.typeLabel,
								warning.issuedAt ? `Issued ${moment(warning.issuedAt).format('D MMM YYYY')}` : '',
							].filter(Boolean).join(' · ');
							const statusLabel = needsAcknowledgement ? 'Acknowledgement required' : 'Acknowledged';

							return (
								<Card
									key={warning.id}
									tone="raised"
									onPress={() => openWarning(warning)}
									accessibilityLabel={[title, meta, statusLabel].filter(Boolean).join(', ')}
								>
									<View style={styles.itemRow}>
										<View style={styles.itemContent}>
											<Text style={styles.itemTitle}>{title}</Text>
											{Boolean(meta) && <Text style={styles.itemMeta}>{meta}</Text>}
											<Tag
												label={statusLabel}
												tone={needsAcknowledgement ? 'danger' : 'success'}
												style={styles.itemStatus}
											/>
										</View>
										<Feather name="chevron-right" size={22} color={colors.iconMuted} />
									</View>
								</Card>
							);
						})}
					</View>
				)
			}
		</Screen>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	list: {
		gap: space.sm,
	},
	itemRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
	},
	itemContent: {
		flex: 1,
		gap: space.xs,
	},
	itemTitle: {
		...type.rowTitleSemibold,
		color: colors.text,
	},
	itemMeta: {
		...type.secondary,
		color: colors.textSecondary,
	},
	itemStatus: {
		marginTop: space.xs,
	},
});

export default MyWarningsView;
